import { useState } from "react";
import axios from "axios";
import { Search, Loader2, CheckCircle, Clock, XCircle, Rocket } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  submitted: { label: "Ingediend", color: "bg-blue-100 text-blue-700 border-blue-200", Icon: Clock },
  in_review: { label: "In beoordeling", color: "bg-yellow-100 text-yellow-700 border-yellow-200", Icon: Clock },
  approved: { label: "Goedgekeurd", color: "bg-green-100 text-green-700 border-green-200", Icon: CheckCircle },
  rejected: { label: "Afgewezen", color: "bg-red-100 text-red-700 border-red-200", Icon: XCircle },
  implemented: { label: "Geïmplementeerd", color: "bg-purple-100 text-purple-700 border-purple-200", Icon: Rocket },
};

const REF_PATTERN = /^DE-\d{8}-[A-F0-9]{6}$/i;

export const ProposalStatusLookup = () => {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async () => {
    const cleaned = ref.trim().toUpperCase();
    if (!REF_PATTERN.test(cleaned)) {
      setError("Voer een geldig referentienummer in (bijv. DE-20260604-ABC123).");
      setResult(null);
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/support/proposal/${cleaned}`);
      setResult(res.data);
    } catch {
      setError("Opzoeken mislukt. Probeer het opnieuw.");
    }
    setLoading(false);
  };

  const meta = result?.proposal?.status ? STATUS_META[result.proposal.status] || STATUS_META.submitted : null;

  return (
    <div className="border-t border-gray-200 p-3 bg-gray-50" data-testid="proposal-status-lookup">
      <p className="text-[10px] font-mono uppercase text-gray-500 mb-1.5">Voorstel-status opvragen</p>
      <div className="flex gap-1.5">
        <input
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="DE-YYYYMMDD-XXXXXX"
          className="flex-1 text-xs border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-[#2d8cf0] font-mono"
          data-testid="proposal-ref-input"
        />
        <button
          onClick={lookup}
          disabled={loading || !ref.trim()}
          className="px-3 py-1.5 bg-[#2d8cf0] text-white text-xs font-bold hover:bg-[#1a6fd4] disabled:opacity-50 flex items-center gap-1"
          data-testid="proposal-lookup-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
        </button>
      </div>
      {error && <p className="mt-1.5 text-[11px] text-red-600">{error}</p>}
      {result && !result.found && (
        <p className="mt-2 text-[11px] text-gray-600" data-testid="proposal-not-found">
          Geen voorstel gevonden met dit referentienummer.
        </p>
      )}
      {result && result.found && meta && (
        <div className="mt-2 p-2 bg-white border border-gray-200" data-testid="proposal-status-result">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-xs font-bold flex-1">{result.proposal.title}</p>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 border ${meta.color}`}>
              <meta.Icon className="w-3 h-3" />
              {meta.label}
            </span>
          </div>
          {result.proposal.summary && (
            <p className="text-[11px] text-gray-600 mt-1">{result.proposal.summary}</p>
          )}
          {result.proposal.review_notes && (
            <p className="text-[11px] text-gray-700 mt-1.5 pt-1.5 border-t border-gray-100">
              <span className="font-bold">Toelichting: </span>{result.proposal.review_notes}
            </p>
          )}
          <p className="text-[10px] font-mono text-gray-400 mt-1.5">
            {result.proposal.proposal_id}
            {result.proposal.submitted_at && ` · ${new Date(result.proposal.submitted_at).toLocaleDateString()}`}
          </p>
        </div>
      )}
    </div>
  );
};
